import { get } from 'lodash';
import { call, put, fork, take } from 'redux-saga/effects';

import {
	SET_FILTER,
	QR_LIST_REQUESTING,
	DISABLED_QR_LIST_REQUESTING,
	QR_LIST_SUCCESS,
	QR_LIST_ERROR,
	QR_DETAIL_REQUESTING,
	QR_DETAIL_SUCCESS,
	QR_DETAIL_ERROR,
	GET_OTP,
	GET_OTP_SUCCESS,
	GET_OTP_ERROR,
	VERIFY_OTP,
	VERIFY_OTP_SUCCESS,
	VERIFY_OTP_ERROR,
	DELETE_NOTIFICATION_RECEIVER,
	DELETE_NOTIFICATION_RECEIVER_ERROR,
	DELETE_NOTIFICATION_RECEIVER_SUCCESS,
	TERMINAL_LIST_REQUESTING,
	TERMINAL_LIST_SUCCESS,
	TERMINAL_LIST_ERROR,
	DISABLED_QR_LIST_SUCCESS,
	DISABLED_QR_LIST_ERROR,
	TOTAL_TERMINAL_LIST_REQUESTING,
	TOTAL_TERMINAL_LIST_SUCCESS,
	UPDATE_QR_DETAILS,
} from '@/redux/ducks/manageQR/types';
import api from '@/data';
import { SHOW_TOAST } from '@/redux/ducks/toast/types';
import { INIT_APP_ERROR } from '@/redux/ducks/init/types';

// Function to get enabled QR list
export function* getQRListFlow(payload) {
	try {
		yield put({ type: SET_FILTER, filters: payload });
		const payloadToSend = { ...payload };
		if (payloadToSend.loadTotalData) {
			delete payloadToSend.loadTotalData;
		}
		const response = yield call(api.getQRList, payloadToSend);
		let successData;
		if (get(response, 'data.data')) {
			successData = {
				...response.data,
				hasNextPage: get(response, 'data.data').length === payloadToSend.limit,
				currentPage: payloadToSend.start / payloadToSend.limit + 1,
				start: payloadToSend.start,
			};
		} else {
			successData = {
				data: [],
				hasNextPage: false,
				currentPage: payloadToSend.start / payloadToSend.limit + 1,
				start: payloadToSend.start,
			};
		}
		yield put({ type: QR_LIST_SUCCESS, data: successData });
	} catch (err) {
		if (err.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: err.data });
		} else {
			yield put({ type: QR_LIST_ERROR, error: err.data });
		}
	}
}

export function* getQRListWatcher() {
	while (true) {
		const { payload } = yield take(QR_LIST_REQUESTING);
		yield fork(getQRListFlow, payload);
	}
}

// Function to get disabled QR list
export function* getDisabledQRListFlow(payload) {
	try {
		const data = {
			...payload,
			getDisabledQr: true,
		};
		yield put({ type: SET_FILTER, filters: payload });
		const response = yield call(api.getQRList, data);
		let successData;
		if (get(response, 'data.data')) {
			successData = {
				...response.data,
				currentPage: payload.start / payload.limit + 1,
				start: payload.start,
			};
		} else {
			successData = {
				data: [],
				currentPage: payload.start / payload.limit + 1,
				start: payload.start,
			};
		}
		yield put({ type: DISABLED_QR_LIST_SUCCESS, data: successData });
	} catch (err) {
		if (err.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: err.data });
		} else {
			yield put({ type: DISABLED_QR_LIST_ERROR, error: err.data });
		}
	}
}

export function* getDisabledQRListWatcher() {
	while (true) {
		const { payload } = yield take(DISABLED_QR_LIST_REQUESTING);
		yield fork(getDisabledQRListFlow, payload);
	}
}

/** Function to get QR detail and QR text
 * @params
 * payload- contains QR detail object
 */
export function* getQRDetailFlow(payload) {
	try {
		const responseQRText = yield call(api.getQRText, payload.id);
		yield put({
			type: QR_DETAIL_SUCCESS,
			data: {
				...payload,
				...responseQRText.data,
			},
		});
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: QR_DETAIL_ERROR, error: error.data });
			yield put({
				type: SHOW_TOAST,
				payload: { msg: get(error, 'data.message'), type: 'error' },
			});
		}
	}
}

export function* getQRDetailWatcher() {
	while (true) {
		const { payload } = yield take(QR_DETAIL_REQUESTING);
		yield fork(getQRDetailFlow, payload);
	}
}

/** Function to get OTP for adding notification receiver number
 * @params
 * payload- contains QR id and mobile number
 */
export function* getOTPFlow(payload) {
	try {
		const response = yield call(
			api.getOTPNotificationReceiver,
			payload.qrCodeId,
			payload.mobileNumber,
		);
		// check whether OTP is required or not
		const otpRequired = response.data.status === 'OTP';
		yield put({
			type: GET_OTP_SUCCESS,
			data: response.data,
			otpRequired,
		});
		if (!otpRequired) {
			yield put({
				type: QR_LIST_REQUESTING,
				payload: {
					merchant: '',
					mappedObjectType: 'QR_CODE',
					start: 0,
					limit: 10,
				},
			});
			yield put({
				type: SHOW_TOAST,
				payload: {
					msg: 'Notification Mobile Number Receiver Added',
					type: 'success',
				},
			});
		}
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: GET_OTP_ERROR, error: error.data });
			if (
				error.status === 400 &&
				get(error, 'data.code') === 'NOTIFICATION_RECEIVER_EXIST'
			) {
				yield put({
					type: SHOW_TOAST,
					payload: {
						msg: 'Notification Mobile Number Receiver Exist',
						type: 'warning',
					},
				});
			}
		}
	}
}

export function* getOTPWatcher() {
	while (true) {
		const { payload } = yield take(GET_OTP);
		yield fork(getOTPFlow, payload);
	}
}

/** Function to verify OTP for adding notification receiver number
 * @params
 * payload- contains QR id, mobile number, token and OTP
 */
export function* verifyOTPFlow(payload) {
	try {
		const response = yield call(
			api.verifyOTPNotificationReceiver,
			payload.qrCodeId,
			payload.phoneNumber,
			payload.token,
			payload.otpNumber,
		);
		yield put({
			type: VERIFY_OTP_SUCCESS,
			data: response.data,
		});
		yield put({
			type: QR_DETAIL_REQUESTING,
			payload: { id: payload.qrCodeId },
		});
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: VERIFY_OTP_ERROR, error: error.data });
		}
	}
}

export function* verifyOTPWatcher() {
	while (true) {
		const { payload } = yield take(VERIFY_OTP);
		yield fork(verifyOTPFlow, payload);
	}
}

/** Function to delete notification reciever number
 * @params
 * payload- contains QR id, mobile number, enable and createdAt
 */
export function* deleteNotificationReceiverFlow(payload) {
	try {
		const response = yield call(
			api.deleteNotificationReceiver,
			payload.qrCodeId,
			payload.payloadData,
		);
		yield put({
			type: DELETE_NOTIFICATION_RECEIVER_SUCCESS,
			data: response.data,
		});
		const index = payload.qrDetails.notificationReceiver?.findIndex(
			(item) => item.phoneNumber === payload.payloadData.phoneNumber,
		);
		if (index >= 0) {
			payload.qrDetails.notificationReceiver.splice(index, 1);
		}
		yield put({
			type: SHOW_TOAST,
			payload: {
				msg: 'Notification Receiver Removed',
				type: 'success',
			},
		});
		yield put({
			type: UPDATE_QR_DETAILS,
			payload: payload,
		});
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({
				type: DELETE_NOTIFICATION_RECEIVER_ERROR,
				error: error.data,
			});
			yield put({
				type: SHOW_TOAST,
				payload: { msg: get(error, 'data.message'), type: 'error' },
			});
		}
	}
}

export function* deleteNotificationReceiverWatcher() {
	while (true) {
		const { payload } = yield take(DELETE_NOTIFICATION_RECEIVER);
		yield fork(deleteNotificationReceiverFlow, payload);
	}
}

/** Function to get terminalId list on API call
 * @params
 * payload- contains merchantId, storeId, skipInactive, limit and start
 */
export function* getTerminalIdList(payload) {
	try {
		const response = yield call(api.getTerminalList, payload);
		if (payload.start === 0) {
			yield put({ type: TERMINAL_LIST_SUCCESS, data: response.data?.data });
		} else {
			yield put({
				type: TOTAL_TERMINAL_LIST_SUCCESS,
				data: response.data?.data,
			});
		}
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: TERMINAL_LIST_ERROR, error: error.data });
		}
	}
}

export function* getTerminalIdWatcher() {
	while (true) {
		const { payload } = yield take(TERMINAL_LIST_REQUESTING);
		yield fork(getTerminalIdList, payload);
	}
}

/** Function to get total terminalId list for infinite scroll
 * @params
 * payload- contains merchantId, storeId, skipInactive, limit and start
 */

export function* getTotalTerminalIdList(payload) {
	try {
		const response = yield call(api.getTerminalList, payload);
		yield put({ type: TOTAL_TERMINAL_LIST_SUCCESS, data: response.data?.data });
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: TERMINAL_LIST_ERROR, error: error.data });
		}
	}
}

export function* getTotalTerminalIdWatcher() {
	while (true) {
		const { payload } = yield take(TOTAL_TERMINAL_LIST_REQUESTING);
		yield fork(getTotalTerminalIdList, payload);
	}
}

function* manageQRFlow() {
	yield fork(getQRListWatcher);
	yield fork(getQRDetailWatcher);
	yield fork(getTerminalIdWatcher);
	yield fork(getOTPWatcher);
	yield fork(verifyOTPWatcher);
	yield fork(deleteNotificationReceiverWatcher);
	yield fork(getDisabledQRListWatcher);
	yield fork(getTotalTerminalIdWatcher);
}

export default manageQRFlow;
